import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import {
  DateRangePicker,
  DateRangePickerProps,
} from "@mui/x-date-pickers-pro/DateRangePicker";
import { MultiInputDateRangeField } from "@mui/x-date-pickers-pro/MultiInputDateRangeField";
import { DateRange, PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";

import { TooltipLabel } from "../../Tooltips";
import Typography from "@mui/material/Typography";
import {
  datePickerVariantSx,
  datePickerDenseSx,
  DatePickerVariant,
} from "./styles";

type CustomDateRangePickerProps = {
  onChange: (value: DateRange<DateTime<boolean>>, type: "date") => void;
  value: DateRange<DateTime<boolean>>;
  disablePast?: boolean;
  fieldSeparator?: ReactNode;
  margin?: string | number;
  marginBetweenFields?: number;
  shortcutsItems?: PickersShortcutsItem<DateRange<DateTime<boolean>>>[];
  closeOnShortcutSelect?: boolean;
  dense?: boolean;
  /** Drop the built-in "Start date" / "End date" labels, for a form that
      labels the pair itself. */
  hideLabels?: boolean;
  variant?: DatePickerVariant;
} & Omit<
  DateRangePickerProps,
  "onChange" | "renderInput" | "value" | "disablePast"
>;

const SHORTCUTS_SX = {
  "& .MuiChip-root": {
    fontSize: "0.8125rem",
    height: "24px",
    borderRadius: "6px",
    backgroundColor: "var(--mui-palette-button-chip)",
    color: "var(--mui-palette-text-primary)",
    "& .MuiChip-label": {
      px: 1,
    },
    "&.MuiChip-clickable:hover": {
      backgroundColor: "var(--mui-palette-button-chipHover)",
    },
  },
};

/**
 * Date range picker — standalone, no Formik.
 * Use in filter bars that need a start + end date, with optional shortcut buttons.
 *
 * - `value` / `onChange`: Luxon DateRange<DateTime>
 * - `variant`: "filled" (default, no border) or "outlined" (with border, no background)
 * - `shortcutsItems`: shortcut chips displayed in the left panel (last N months, year…)
 * - `fieldSeparator`: React node rendered between the two fields
 * - `dense`: when true, keeps the shrunk label at MUI native position instead of floating above the input
 * - `hideLabels`: when true, renders no field labels at all
 */
const CustomDateRangePicker = (props: CustomDateRangePickerProps) => {
  const {
    onChange,
    value,
    disablePast,
    fieldSeparator,
    margin,
    marginBetweenFields = 16,
    shortcutsItems,
    closeOnShortcutSelect = true,
    dense = false,
    hideLabels = false,
    variant = "filled",
    ...restProps
  } = props;
  const intl = useIntl();

  const handleChange = (newValue: DateRange<DateTime<boolean>>) => {
    const [start, end] = newValue;
    // Only propagate when both dates are valid or null to avoid
    // mid-typing resets caused by the parent re-rendering with a new value prop
    if ((!start || start.isValid) && (!end || end.isValid)) {
      onChange(newValue, "date");
    }
  };

  return (
    <DateRangePicker
      sx={{ margin: margin ?? "18px 0 16px 0" }}
      {...restProps}
      disablePast={disablePast}
      onChange={handleChange}
      slots={{
        field: MultiInputDateRangeField,
      }}
      slotProps={{
        shortcuts: shortcutsItems
          ? {
              items: shortcutsItems,
              changeImportance: closeOnShortcutSelect ? "accept" : "set",
              sx: SHORTCUTS_SX,
            }
          : undefined,
        field: {
          sx: { alignItems: "center" },
          slots: {
            separator:
              fieldSeparator == null
                ? () => null
                : () => <Typography>{fieldSeparator}</Typography>,
          },
          slotProps: {
            textField: ({ position }: { position: "start" | "end" }) =>
              position === "start"
                ? {
                    sx: [
                      datePickerVariantSx[variant],
                      ...(dense ? [datePickerDenseSx] : []),
                      { minWidth: 0 },
                    ],
                    label: hideLabels
                      ? undefined
                      : intl.formatMessage({ id: "DATE_START" }),
                    variant: "outlined",
                    ...(dense && {
                      slotProps: { inputLabel: { shrink: true } },
                    }),
                  }
                : {
                    sx: [
                      datePickerVariantSx[variant],
                      ...(dense ? [datePickerDenseSx] : []),
                      {
                        marginLeft: `${marginBetweenFields}px !important`,
                        minWidth: 0,
                      },
                    ],
                    label: hideLabels ? undefined : (
                      <TooltipLabel
                        label={intl.formatMessage({ id: "DATE_END" })}
                        tooltipLabel={"DATE_INCLUDED"}
                      />
                    ),
                    variant: "outlined",
                    ...(dense && {
                      slotProps: { inputLabel: { shrink: true } },
                    }),
                  },
          },
        } as any,
      }}
      value={value}
    />
  );
};

export default CustomDateRangePicker;
